import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  FileBarChart, Search, Calendar, IndianRupee, Users, 
  Clock, CheckCircle, XCircle, AlertCircle, Share2, Trash2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/utils/dateFormat';

/**
 * CLIENT QUOTATIONS SENT PAGE (Admin View)
 * Shows quotations that have been published/sent to clients
 */
export default function ClientQuotationsSent() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${API}/quotations`);
      // Filter to show only quotations sent to clients (client_visible = true)
      const clientQuotations = response.data.filter(q => q.client_visible);
      setQuotations(clientQuotations);
    } catch (error) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteReason.trim() || deleteReason.trim().length < 5) {
      toast.error('Please provide a reason (at least 5 characters)');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/quotations/${selectedQuotation.id}`, {
        data: { reason: deleteReason.trim() }
      });
      toast.success('Quotation permanently deleted');
      setDeleteDialogOpen(false);
      setSelectedQuotation(null);
      setDeleteReason('');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quotation');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'pending': case 'draft': return 'bg-amber-100 text-amber-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const filteredQuotations = quotations.filter(q => 
    q.quotation_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.project_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-stone-400">Loading quotations...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-quotations-sent">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <Share2 className="text-blue-600" size={24} />
              Quotations Sent to Clients
            </h1>
            <p className="text-stone-500 text-sm">Quotations that have been published and are visible to clients</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search quotations..."
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-blue-600">{quotations.length}</p>
            <p className="text-sm text-stone-500">Total Sent</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-amber-600">
              {quotations.filter(q => q.status === 'pending').length}
            </p>
            <p className="text-sm text-stone-500">Pending Review</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-green-600">
              {quotations.filter(q => q.status === 'accepted').length}
            </p>
            <p className="text-sm text-stone-500">Accepted</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-stone-600">
              ₹{(quotations.reduce((sum, q) => sum + (q.client_total || 0), 0) / 100000).toFixed(1)}L
            </p>
            <p className="text-sm text-stone-500">Total Value (with Markup)</p>
          </div>
        </div>

        {/* Quotations List */}
        {filteredQuotations.length === 0 ? (
          <div className="apple-card p-8 text-center">
            <FileBarChart className="mx-auto mb-4 text-stone-300" size={48} />
            <p className="text-stone-500">No quotations sent to clients yet</p>
            <p className="text-sm text-stone-400 mt-1">Publish quotations from the main Quotations page</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredQuotations.map((quot) => (
              <div key={quot.id} className="apple-card p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{quot.quotation_number}</h3>
                      <Badge className={getStatusColor(quot.status)}>
                        {quot.status}
                      </Badge>
                      <Badge className="bg-blue-100 text-blue-700">
                        <Share2 size={12} className="mr-1" />
                        Published
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-500">
                      {quot.project_name || 'No project'} • {quot.items?.length || 0} items
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        Published {formatDate(new Date(quot.client_published_at))}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-stone-400">Base Amount</p>
                      <p className="font-medium">₹{(quot.total || 0).toLocaleString()}</p>
                      <p className="text-sm text-stone-400 mt-2">Client Amount</p>
                      <p className="font-bold text-blue-600">₹{(quot.client_total || 0).toLocaleString()}</p>
                    </div>
                    <button
                      onClick={() => { setSelectedQuotation(quot); setDeleteDialogOpen(true); }}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete quotation"
                      data-testid={`delete-sent-quotation-${quot.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteReason(''); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 size={20} />
                Permanently Delete Quotation
              </AlertDialogTitle>
              <AlertDialogDescription>
                <div className="space-y-3">
                  <p>
                    This will permanently delete <strong>{selectedQuotation?.quotation_number}</strong>.
                    This action cannot be undone.
                  </p>
                  <div>
                    <label className="text-sm font-medium text-stone-700">
                      Reason for deletion (required)
                    </label>
                    <Textarea
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      placeholder="Enter reason for deleting this quotation..."
                      className="mt-1"
                      data-testid="delete-reason-input"
                    />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteReason('')}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting || deleteReason.trim().length < 5}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
